import { PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Divider,
  Flex,
  List,
  Popover,
  Table,
  TableColumnsType,
  Tag
} from 'antd'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useStocksServiceGetInquiryListEndpoint } from 'src/api/queries'
import {
  InventoryDocumentItemResponse,
  InventoryDocumentState,
  Permissions
} from 'src/api/requests'
import { PageBreadcrumbs } from 'src/components'
import { getStatus, SelectedItemsState } from 'src/features/stock'
import { ApplicationFilters } from 'src/features/stock/components/applications'
import { useUser, useTablePagination } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

const VISIBLE_TAG_COUNT = 2

const MapValuesToIds = (data: SelectedItemsState) => {
  const { userData, hasPermission } = useUser()

  const isStockAdmin = hasPermission(Permissions.STOCKS_MENU_ACCESS_ISSUANCE)

  const legalEntityIds = !isStockAdmin
    ? [userData?.currentLegalEntityId ?? '']
    : data?.legalEntityIds?.values

  const filteredLegalEntityIds = legalEntityIds?.filter((id) => id !== '')

  return {
    ...(filteredLegalEntityIds?.length
      ? { legalEntityIds: filteredLegalEntityIds }
      : {}),

    internationalNameId: data?.internationalNameId?.values?.[0],
    fromDate: data?.inDate?.values?.[0],
    toDate: data?.inDate?.values?.[1],
    searchTerm: data?.search?.values?.[0],
    states: isStockAdmin
      ? ((data?.states?.values as InventoryDocumentState[]) ??
        Object.values(InventoryDocumentState).filter(
          (state) => state !== InventoryDocumentState.NEW
        ))
      : (data?.states?.values as InventoryDocumentState[])
  }
}

export const StockApplicationsPage = () => {
  const [selectedItems, setSelectedItems] = useState<SelectedItemsState>({})

  const navigate = useNavigate()
  const { hasPermission } = useUser()
  const { t } = useLocales()

  const pagination = useTablePagination()

  const isStockAdmin = hasPermission(Permissions.STOCKS_MENU_ACCESS_ISSUANCE)

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...MapValuesToIds(selectedItems)
  }

  const { data, isLoading } =
    useStocksServiceGetInquiryListEndpoint(queryParams)

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'documentNumber',
        title: t('stock_applications.number'),
        render: ({ documentNumber }) => documentNumber
      },
      {
        key: 'documentDate',
        title: t('stock_applications.request_date'),
        render: ({ documentDate }) =>
          dayjs(documentDate).format(dayMonthYearBackSlash)
      },
      {
        key: 'beneficiary',
        title: t('stock_applications.juridic_person'),
        dataIndex: ['beneficiary', 'name']
      },
      {
        key: 'inquiryItems',
        title: t('stock_applications.consumables'),
        render: ({ items }: { items: InventoryDocumentItemResponse[] }) => {
          if (!items?.length) return '-'

          const extraCount = items?.length - VISIBLE_TAG_COUNT

          return (
            <Flex>
              <List
                itemLayout="horizontal"
                dataSource={items}
                renderItem={(item, index) =>
                  index < VISIBLE_TAG_COUNT && (
                    <Tag
                      key={item?.id}
                    >{`${item?.name} (${item?.quantity})`}</Tag>
                  )
                }
              />
              <Popover
                content={
                  <Flex vertical gap={4}>
                    {items?.slice(VISIBLE_TAG_COUNT).map((item) => (
                      <Tag
                        key={item?.id}
                      >{`${item?.name} (${item?.quantity})`}</Tag>
                    ))}
                  </Flex>
                }
              >
                {extraCount > 0 && <Tag className="pointer">+{extraCount}</Tag>}
              </Popover>
            </Flex>
          )
        }
      },
      {
        key: 'state',
        title: t('form.status'),
        render: ({ state }) => {
          return (
            <Tag color={getStatus(t, state).color} key={state}>
              {getStatus(t, state).text}
            </Tag>
          )
        }
      },
      {
        key: 'details',
        align: 'center',
        width: 103,
        render: ({ id, state }) => {
          const isNewStock = state === InventoryDocumentState.NEW

          const route = [
            !isStockAdmin && isNewStock && paths.stock.applicationEdit(id),
            !isStockAdmin && !isNewStock && paths.stock.applicationDetails(id),
            isStockAdmin && paths.stockAdmin.issuanceDetails(id)
          ].find(Boolean)

          return route ? <Link to={route}>{t('common.details')}</Link> : null
        }
      }
    ],
    [t]
  )

  return (
    <PageBreadcrumbs
      title={t('stock.applications')}
      items={[
        { title: t('menu.consumable_medicaments') },
        { title: t('stock.applications') }
      ]}
    >
      <Flex gap={16}>
        <ApplicationFilters
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
        />
        {!isStockAdmin && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(paths.stock.applicationCreate)}
          >
            {t('stock_applications.create_new_application')}
          </Button>
        )}
      </Flex>

      <Divider />

      <Table
        rowKey="id"
        columns={columns}
        loading={isLoading}
        dataSource={data?.data}
        scroll={{ x: 'max-content' }}
        pagination={{ ...pagination, total: data?.totalCount }}
      />
    </PageBreadcrumbs>
  )
}
